import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";

// helper: read file → base64 (no data: prefix)
const fileToB64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Read error"));
    r.onload = () => {
      const s = r.result || "";
      resolve(String(s).split(",")[1] || "");
    };
    r.readAsDataURL(file);
  });

const MAX_MB = 5;
const DOC_MAX_MB = {
  internship_letter: 5,
  offer_letter: 5,
  appointment_letter: 20,
};

const empty = {
  // Personal
  employee_name: "",
  food_preference: "",
  designation: "",
  department: "",
  reporting_manager: "",
  date_of_joining: "",
  gender: "",
  date_of_birth: "",
  blood_group: "",
  marital_status: "",
  spouse_name: "",
  fathers_name: "",
  mothers_name: "",
  // Contact
  personal_contact_1: "",
  personal_contact_2: "",
  official_email: "",
  personal_email: "",
  current_address: "",
  permanent_address: "",
  highest_qualification: "",
  last_company: "",
  // Emergency
  emergency_name: "",
  emergency_number: "",
  emergency_relation: "",
  // Gov IDs
  pan_number: "",
  aadhaar: "",
  uan: "",
  // Bank
  bank_name: "",
  bank_account_number: "",
  ifsc_code: "",
  bank_branch: "",
  // BGV
  bgv_status: "",
  bgv_action: "",
  // Status
  employment_status: "active",
};

export default function EmployeeOnboarding() {
  const { employeeDbId } = useParams();
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [existing, setExisting] = useState(null);
  const [docs, setDocs] = useState({
    internship_letter: null,
    offer_letter: null,
    appointment_letter: null,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/employees/${employeeDbId}`);
        setExisting(r.data);
        // Preload known fields (seeded at creation from Roster)
        setForm({
          ...empty,
          employee_name: r.data.employee_name || "",
          food_preference: r.data.food_preference || "",
          designation: r.data.designation || "",
          department: r.data.department || "",
          reporting_manager: r.data.reporting_manager || "",
          date_of_joining: r.data.date_of_joining || "",
          gender: r.data.gender || "",
          date_of_birth: r.data.date_of_birth || "",
          blood_group: r.data.blood_group || "",
          marital_status: r.data.marital_status || "",
          spouse_name: r.data.spouse_name || "",
          fathers_name: r.data.fathers_name || "",
          mothers_name: r.data.mothers_name || "",
          personal_contact_1: r.data.personal_contact_1 || "",
          personal_contact_2: r.data.personal_contact_2 || "",
          official_email: r.data.official_email || "",
          personal_email: r.data.personal_email || "",
          current_address: r.data.current_address || "",
          permanent_address: r.data.permanent_address || "",
          highest_qualification: r.data.highest_qualification || "",
          last_company: r.data.last_company || "",
          emergency_name: r.data.emergency_name || "",
          emergency_number: r.data.emergency_number || "",
          emergency_relation: r.data.emergency_relation || "",
          pan_number: r.data.pan_number || "",
          aadhaar: r.data.aadhaar || "",
          uan: r.data.uan || "",
          bank_name: r.data.bank_name || "",
          bank_account_number: r.data.bank_account_number || "",
          ifsc_code: r.data.ifsc_code || "",
          bank_branch: r.data.bank_branch || "",
          bgv_status: r.data.bgv_status || "",
          bgv_action: r.data.bgv_action || "",
          employment_status: r.data.employment_status || "active",
        });
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load employee");
      }
    })();
  }, [employeeDbId]);

  if (err) return <div className="p-8 text-error">{err}</div>;
  if (!existing) return <div className="p-8 text-quiet">Loading…</div>;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onPickFile = (key) => async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const cap = DOC_MAX_MB[key] ?? MAX_MB;
    if (file.size > cap * 1024 * 1024) {
      toast.error(`${file.name} exceeds ${cap} MB`);
      ev.target.value = "";
      return;
    }
    try {
      const b64 = await fileToB64(file);
      setDocs((d) => ({ ...d, [key]: { filename: file.name, b64 } }));
    } catch {
      toast.error(`Failed to read ${file.name}`);
    }
  };

  const clearFile = (key) => setDocs((d) => ({ ...d, [key]: null }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.employee_name.trim()) return toast.error("Employee name is required");
    if (!form.date_of_joining) return toast.error("Date of joining is required");

    setBusy(true);
    try {
      const payload = {
        ...form,
        food_preference: form.food_preference || null,
        gender: form.gender || null,
        bgv_status: form.bgv_status || null,
        date_of_birth: form.date_of_birth || null,
        official_email: form.official_email || null,
        personal_email: form.personal_email || null,
        internship_letter: docs.internship_letter,
        offer_letter: docs.offer_letter,
        appointment_letter: docs.appointment_letter,
      };
      // Strip empty strings to null for optional fields to satisfy Pydantic types
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      await api.put(`/employees/${employeeDbId}/onboarding`, payload);
      toast.success("Onboarding saved");
      nav(`/employees/${employeeDbId}`);
    } catch (er) {
      toast.error(er?.response?.data?.detail || "Failed to save onboarding");
    } finally {
      setBusy(false);
    }
  };

  const isEdit = !!existing.onboarding_completed_at;

  return (
    <div className="p-8 max-w-[1200px] mx-auto" data-testid="employee-onboarding-page">
      <div className="border-b border-rule pb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
          Employee · {existing.employee_id}
        </div>
        <h1 className="font-serif text-4xl text-ink mt-2 leading-none">
          {isEdit ? "Edit onboarding" : "Onboarding"}
        </h1>
        <p className="text-sm text-quiet mt-3">
          Fill all applicable fields. Onboarding must be completed in one submission — no drafts.
        </p>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-8">
        <Section title="1 · Personal">
          <Grid>
            <Field label="Employee ID"><ReadOnly value={existing.employee_id} /></Field>
            <Field label="Employee Name *"><Input testId="in-employee-name" value={form.employee_name} onChange={set("employee_name")} required /></Field>
            <Field label="Food Preference">
              <Select testId="in-food" value={form.food_preference} onChange={set("food_preference")} options={[["","—"],["veg","Vegetarian"],["non_veg","Non-Vegetarian"]]} />
            </Field>
            <Field label="Designation"><Input testId="in-designation" value={form.designation} onChange={set("designation")} /></Field>
            <Field label="Department"><Input testId="in-department" value={form.department} onChange={set("department")} /></Field>
            <Field label="Reporting Manager"><Input testId="in-mgr" value={form.reporting_manager} onChange={set("reporting_manager")} /></Field>
            <Field label="Date of Joining *"><Input testId="in-doj" type="date" value={form.date_of_joining} onChange={set("date_of_joining")} required /></Field>
            <Field label="Gender">
              <Select testId="in-gender" value={form.gender} onChange={set("gender")} options={[["","—"],["male","Male"],["female","Female"],["other","Other"]]} />
            </Field>
            <Field label="Date of Birth"><Input testId="in-dob" type="date" value={form.date_of_birth} onChange={set("date_of_birth")} /></Field>
            <Field label="Blood Group"><Input testId="in-blood" value={form.blood_group} onChange={set("blood_group")} placeholder="e.g. O+" /></Field>
            <Field label="Marital Status">
              <Select testId="in-marital" value={form.marital_status} onChange={set("marital_status")} options={[["","—"],["single","Single"],["married","Married"],["divorced","Divorced"],["widowed","Widowed"]]} />
            </Field>
            <Field label="Spouse Name"><Input testId="in-spouse" value={form.spouse_name} onChange={set("spouse_name")} /></Field>
            <Field label="Father's Name"><Input testId="in-father" value={form.fathers_name} onChange={set("fathers_name")} /></Field>
            <Field label="Mother's Name"><Input testId="in-mother" value={form.mothers_name} onChange={set("mothers_name")} /></Field>
          </Grid>
        </Section>

        <Section title="2 · Contact">
          <Grid>
            <Field label="Personal Contact 1"><Input testId="in-pc1" value={form.personal_contact_1} onChange={set("personal_contact_1")} /></Field>
            <Field label="Personal Contact 2"><Input testId="in-pc2" value={form.personal_contact_2} onChange={set("personal_contact_2")} /></Field>
            <Field label="Official Email"><Input testId="in-off-email" type="email" value={form.official_email} onChange={set("official_email")} /></Field>
            <Field label="Personal Email"><Input testId="in-pers-email" type="email" value={form.personal_email} onChange={set("personal_email")} /></Field>
            <Field label="Current Address" full><Textarea testId="in-cur-addr" value={form.current_address} onChange={set("current_address")} /></Field>
            <Field label="Permanent Address" full><Textarea testId="in-perm-addr" value={form.permanent_address} onChange={set("permanent_address")} /></Field>
            <Field label="Highest Qualification"><Input testId="in-qual" value={form.highest_qualification} onChange={set("highest_qualification")} /></Field>
            <Field label="Last Company"><Input testId="in-lastco" value={form.last_company} onChange={set("last_company")} /></Field>
          </Grid>
        </Section>

        <Section title="3 · Emergency Contact">
          <Grid>
            <Field label="Name"><Input testId="in-emg-name" value={form.emergency_name} onChange={set("emergency_name")} /></Field>
            <Field label="Number"><Input testId="in-emg-num" value={form.emergency_number} onChange={set("emergency_number")} /></Field>
            <Field label="Relation"><Input testId="in-emg-rel" value={form.emergency_relation} onChange={set("emergency_relation")} /></Field>
          </Grid>
        </Section>

        <Section title="4 · Government IDs" hint="Encrypted at rest (Fernet). Not visible in raw DB.">
          <Grid>
            <Field label="PAN Number"><Input testId="in-pan" value={form.pan_number} onChange={set("pan_number")} placeholder="ABCDE1234F" /></Field>
            <Field label="Aadhaar"><Input testId="in-aadhaar" value={form.aadhaar} onChange={set("aadhaar")} placeholder="12-digit" /></Field>
            <Field label="UAN"><Input testId="in-uan" value={form.uan} onChange={set("uan")} /></Field>
          </Grid>
        </Section>

        <Section title="5 · Bank Details" hint="Account number encrypted at rest.">
          <Grid>
            <Field label="Bank Name"><Input testId="in-bank" value={form.bank_name} onChange={set("bank_name")} /></Field>
            <Field label="Account Number"><Input testId="in-bank-acc" value={form.bank_account_number} onChange={set("bank_account_number")} /></Field>
            <Field label="IFSC Code"><Input testId="in-ifsc" value={form.ifsc_code} onChange={set("ifsc_code")} /></Field>
            <Field label="Branch"><Input testId="in-branch" value={form.bank_branch} onChange={set("bank_branch")} /></Field>
          </Grid>
        </Section>

        <Section title="6 · BGV">
          <Grid>
            <Field label="BGV Status">
              <Select testId="in-bgv-status" value={form.bgv_status} onChange={set("bgv_status")} options={[["","—"],["red","Red"],["amber","Amber"],["green","Green"]]} />
            </Field>
            <Field label="BGV Action" full><Textarea testId="in-bgv-action" value={form.bgv_action} onChange={set("bgv_action")} /></Field>
          </Grid>
        </Section>

        <Section title="7 · Documents" hint={`PDF/DOC/JPG · Internship & Offer max ${DOC_MAX_MB.internship_letter} MB · Appointment max ${DOC_MAX_MB.appointment_letter} MB`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FileField testId="in-doc-intern" label={`Internship Letter (optional · max ${DOC_MAX_MB.internship_letter} MB)`} existing={existing.documents?.internship_letter} picked={docs.internship_letter} onPick={onPickFile("internship_letter")} onClear={() => clearFile("internship_letter")} />
            <FileField testId="in-doc-offer" label={`Offer Letter (max ${DOC_MAX_MB.offer_letter} MB)`} existing={existing.documents?.offer_letter} picked={docs.offer_letter} onPick={onPickFile("offer_letter")} onClear={() => clearFile("offer_letter")} />
            <FileField testId="in-doc-appt" label={`Appointment Letter (max ${DOC_MAX_MB.appointment_letter} MB)`} existing={existing.documents?.appointment_letter} picked={docs.appointment_letter} onPick={onPickFile("appointment_letter")} onClear={() => clearFile("appointment_letter")} />
          </div>
        </Section>

        <Section title="8 · Employment Status">
          <Grid>
            <Field label="Status">
              <Select testId="in-emp-status" value={form.employment_status} onChange={set("employment_status")} options={[["active","Active"],["inactive","Inactive"]]} />
            </Field>
          </Grid>
        </Section>

        <div className="sticky bottom-0 bg-white border-t border-rule -mx-8 px-8 py-4 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => nav(`/employees/${employeeDbId}`)}
            className="border border-ink px-4 py-2 text-sm hover:bg-paper"
            data-testid="onboard-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="onboard-submit-btn"
            className="bg-klein hover:bg-klein-hover text-white px-6 py-2 text-sm font-medium disabled:opacity-50"
          >
            {busy ? "Saving…" : isEdit ? "Save changes" : "Submit onboarding"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Section = ({ title, hint, children }) => (
  <section className="bg-white border border-rule">
    <div className="px-5 py-4 border-b border-rule flex items-baseline justify-between">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{title}</div>
      {hint && <div className="font-mono text-[10px] text-quiet">{hint}</div>}
    </div>
    <div className="p-5">{children}</div>
  </section>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">{children}</div>
);

const Field = ({ label, full, children }) => (
  <div className={full ? "md:col-span-3" : ""}>
    <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-1">{label}</div>
    {children}
  </div>
);

const Input = ({ testId, ...p }) => (
  <input data-testid={testId} {...p} className="w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white" />
);
const Textarea = ({ testId, ...p }) => (
  <textarea data-testid={testId} rows={3} {...p} className="w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white" />
);
const Select = ({ testId, options, ...p }) => (
  <select data-testid={testId} {...p} className="w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white">
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);
const ReadOnly = ({ value }) => (
  <div className="w-full border border-rule bg-paper px-3 py-2 text-sm text-quiet font-mono">{value || "—"}</div>
);

const FileField = ({ testId, label, existing, picked, onPick, onClear }) => {
  const ref = useRef(null);
  return (
    <div className="border border-rule p-3 bg-paper">
      <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">{label}</div>
      {picked ? (
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="text-xs text-ink truncate" title={picked.filename}>📄 {picked.filename}</div>
          <button type="button" onClick={() => { onClear(); if (ref.current) ref.current.value = ""; }} className="text-[10px] font-mono uppercase text-error hover:underline">Remove</button>
        </div>
      ) : existing ? (
        <div className="text-xs text-quiet mb-2">Current: <span className="text-ink">{existing.filename}</span></div>
      ) : (
        <div className="text-xs text-quiet mb-2">No file uploaded</div>
      )}
      <input
        ref={ref}
        data-testid={testId}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        onChange={onPick}
        className="text-xs w-full"
      />
    </div>
  );
};
